/**
 * ResearchEdge — custom bezier edge for the research graph.
 *
 * Edge styling aligned with research_ui_v2 handoff §6.2:
 *  - default   — border-strong grey, 1.5px
 *  - internal  — node-definition (purple), 2px
 *  - conditional — warning (orange), 2.5px
 *  - hierarchy   — border-default dashed, 1px, 60% opacity
 *  - on highlight path — accent (teal) 2px + drop-shadow glow +
 *                        midpoint traversal dot
 *
 * Rest-state visibility (UI polish batch 1, task 7): both the stroke and
 * the edge label render at full opacity at rest. Hover is a visual no-op
 * (still used for z-index stacking so overlapping sibling labels read
 * clearly when the user mouses over). Click / selected-path behaviour is
 * unchanged — it still recolours to teal and shows arrow markers.
 */

import React, { memo, useContext } from 'react';
import { getBezierPath, EdgeLabelRenderer } from 'reactflow';
import type { EdgeProps } from 'reactflow';
import { HighlightContext } from '../shared/HighlightContext';
import { HoverContext } from '../shared/HoverContext';
import { EdgeLabelsContext } from '../cluster/EdgeLabelsContext';
import { ClusterHeaderRectsContext } from '../cluster/ClusterHeaderRectsContext';
import type { HeaderRect } from '../cluster/ClusterHeaderRectsContext';

export interface ResearchEdgeData {
  label?: string;
  isInternal?: boolean;
  isHierarchy?: boolean;
  isConditional?: boolean;
}

// Edge stroke colors. Brand and structural tokens resolve via CSS variables so
// they track palette overhauls automatically; graph-vocabulary colors (purple
// for internal/definition edges, amber for conditional edges) stay as literal
// hex per the R5 carve-out — node/edge type colors are intentional semantic
// anchors documented in DESIGN.md, not brand colors.
const C_DEFAULT = 'var(--border-strong)';
const C_HIERARCHY = 'var(--border-default)';
const C_INTERNAL = '#7c3aed';      // node-definition (purple) — graph vocab
const C_CONDITIONAL = '#f59e0b';   // warning (orange)            — graph vocab
const C_ACCENT = 'var(--secondary)';  // teal highlight path — pinned to secondary so it stays warm regardless of where --accent points (matches .graph-node.is-active)
const C_FG_SECONDARY = 'var(--fg-secondary)';

function ResearchEdgeComponent({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}: EdgeProps<ResearchEdgeData>) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  const edgeLabel = data?.label || '';
  const isInternal = data?.isInternal ?? false;
  const isHierarchy = data?.isHierarchy ?? false;
  const isConditional = data?.isConditional ?? false;

  const { visible } = useContext(EdgeLabelsContext);
  const highlight = useContext(HighlightContext);
  const isOnPath = highlight.active && highlight.edgeKeys.has(id);
  const isDimmed = highlight.active && !isOnPath;

  // Over-header ghosting: when a CLUSTER (its header) is selected, the header is
  // the focus — so the part of a highlighted arrow that crosses a cluster header
  // shows at 30% over it (the rest stays 100%). Headers sit BELOW the cards in z
  // (header < card), so this can't be a z trick: the elevated edge already paints
  // above everything, and we instead draw it twice — clipped to OUTSIDE the
  // headers at 100%, and clipped to INSIDE the headers at 30%. A node selection
  // (`mode === 'node'`) gets no ghost: the arrow paints fully above the header.
  const headerRects = useContext(ClusterHeaderRectsContext);
  const wantsGhost = isOnPath && highlight.mode === 'cluster';
  // Mask field = the edge's endpoint bounding box, padded. In the top-down
  // tiered layout, downward bezier control points stay within this box, so a
  // tight field reliably covers the path (and keeps the clip cheap).
  const GHOST_PAD = 80;
  const fx = Math.min(sourceX, targetX) - GHOST_PAD;
  const fy = Math.min(sourceY, targetY) - GHOST_PAD;
  const fw = Math.abs(targetX - sourceX) + GHOST_PAD * 2;
  const fh = Math.abs(targetY - sourceY) + GHOST_PAD * 2;
  const ghostRects: HeaderRect[] = wantsGhost
    ? headerRects.filter(
        (r) => r.x < fx + fw && r.x + r.width > fx && r.y < fy + fh && r.y + r.height > fy,
      )
    : [];
  const applyGhost = ghostRects.length > 0;
  const clipOutId = `${id}-ghost-out`;
  const clipInId = `${id}-ghost-in`;
  const rectToPath = (r: HeaderRect) => `M${r.x},${r.y} h${r.width} v${r.height} h${-r.width} Z`;
  // evenodd: outer field rect minus the header rects = the area OUTSIDE headers.
  const fieldMinusHeaders = [
    rectToPath({ x: fx, y: fy, width: fw, height: fh }),
    ...ghostRects.map(rectToPath),
  ].join(' ');

  // Labels are fully visible at rest (UI polish batch 1, task 7). Hover is
  // a visual no-op — it no longer dims or brightens the label. The endpoint-
  // hover signal is still read so the label can jump to a higher z-index and
  // win the stacking contest when sibling decision-point labels overlap.
  const hoveredNodeId = useContext(HoverContext);
  const isEndpointHovered = !!hoveredNodeId && (hoveredNodeId === source || hoveredNodeId === target);
  const labelOpacity = isDimmed ? 0.12 : 1;

  // Decide stroke look by priority: highlight-path > hierarchy > internal > conditional > default
  let strokeColor = C_DEFAULT;
  let strokeWidth = 1.5;
  let strokeDasharray: string | undefined;
  let pathFilter: string | undefined;

  if (isOnPath) {
    strokeColor = C_ACCENT;
    strokeWidth = 2;
    pathFilter = `drop-shadow(0 0 4px color-mix(in srgb, ${C_ACCENT} 35%, transparent))`;
  } else if (isHierarchy) {
    strokeColor = C_HIERARCHY;
    strokeWidth = 1;
    strokeDasharray = '6 4';
  } else if (isInternal) {
    strokeColor = C_INTERNAL;
    strokeWidth = 2;
  } else if (isConditional) {
    strokeColor = C_CONDITIONAL;
    strokeWidth = 2.5;
  }

  const opacity = isDimmed ? 0.15 : isHierarchy ? 0.6 : 1;

  const pathStyle: React.CSSProperties = {
    stroke: strokeColor,
    strokeWidth,
    strokeDasharray,
    fill: 'none',
    filter: pathFilter,
  };

  return (
    <g style={{ opacity, transition: 'opacity 0.3s ease' }}>
      {/* Arrow marker — matches stroke color */}
      <defs>
        <marker
          id={`${id}-arrow`}
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="8"
          markerHeight="8"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={strokeColor} />
        </marker>
        {applyGhost && (
          <>
            <clipPath id={clipOutId} clipPathUnits="userSpaceOnUse">
              <path d={fieldMinusHeaders} clipRule="evenodd" />
            </clipPath>
            <clipPath id={clipInId} clipPathUnits="userSpaceOnUse">
              {ghostRects.map((r, i) => (
                <rect key={i} x={r.x} y={r.y} width={r.width} height={r.height} />
              ))}
            </clipPath>
          </>
        )}
      </defs>

      {applyGhost ? (
        <>
          {/* Outside the headers: the arrow at full strength. */}
          <path
            id={id}
            className="react-flow__edge-path"
            d={edgePath}
            style={pathStyle}
            clipPath={`url(#${clipOutId})`}
            markerEnd={`url(#${id}-arrow)`}
          />
          {/* Over the headers: the same arrow ghosted to 30% (no glow), so the
              selected header still reads but the connection stays legible. */}
          <path
            className="react-flow__edge-path"
            d={edgePath}
            style={{ ...pathStyle, filter: undefined, opacity: 0.3 }}
            clipPath={`url(#${clipInId})`}
            markerEnd={`url(#${id}-arrow)`}
          />
        </>
      ) : (
        <path
          id={id}
          className="react-flow__edge-path"
          d={edgePath}
          style={pathStyle}
          markerEnd={`url(#${id}-arrow)`}
        />
      )}

      {/* Midpoint traversal dot on highlight path (teal). Ghosted the same way
          as the stroke: when a cluster is selected and the midpoint lands over a
          header, the dot drops to 30% (no glow) so it doesn't pop on top of the
          header the spec wants to keep in focus. */}
      {isOnPath && (
        applyGhost ? (
          <>
            <circle
              cx={labelX}
              cy={labelY}
              r={3}
              clipPath={`url(#${clipOutId})`}
              style={{
                fill: C_ACCENT,
                filter: `drop-shadow(0 0 3px color-mix(in srgb, ${C_ACCENT} 40%, transparent))`,
              }}
            />
            <circle
              cx={labelX}
              cy={labelY}
              r={3}
              clipPath={`url(#${clipInId})`}
              style={{ fill: C_ACCENT, opacity: 0.3 }}
            />
          </>
        ) : (
          <circle
            cx={labelX}
            cy={labelY}
            r={3}
            style={{
              fill: C_ACCENT,
              filter: `drop-shadow(0 0 3px color-mix(in srgb, ${C_ACCENT} 40%, transparent))`,
            }}
          />
        )
      )}

      {/* Edge label pill — opacity driven by its own labelOpacity (not
          the <g> opacity) so the label can dim independently from the
          stroke. Lit labels also jump to a higher z-index so a hovered
          label that sits under a sibling still reads clearly. */}
      {visible && edgeLabel && (
        <EdgeLabelRenderer>
          <div
            className="font-mono px-2 py-0.5 rounded-full bg-surface border border-border-default shadow-bark-xs text-[10px] font-medium whitespace-nowrap"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
              color: isOnPath ? C_ACCENT : isConditional ? C_CONDITIONAL : C_FG_SECONDARY,
              opacity: labelOpacity,
              // Lit labels jump to the top of the stack; idle labels stay
              // in DOM order. Dimmed labels drop below both.
              zIndex: (isOnPath || isEndpointHovered) ? 20 : isDimmed ? 0 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            {edgeLabel}
          </div>
        </EdgeLabelRenderer>
      )}
    </g>
  );
}

export default memo(ResearchEdgeComponent);
